import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import { PermissionWrapper } from "../../helpers/permission.js";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("authToken");

  // Fetch roles with pagination and search
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}hrms/role`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: search,
          page: page,
          limit: limit,
        },
      });

      if (response.status === 200) {
        setRoles(response.data.roles || []);
        const totalCount = response.data.total || response.data.roles?.length || 0;
        setTotalRecords(totalCount);
        setTotalPages(Math.ceil(totalCount / limit));
      } else {
        Swal.fire("Error", "Failed to fetch roles", "error");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      Swal.fire("Error", "Failed to fetch roles", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [page, search, limit]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(id);
      }
    });
  };

  // Handle delete operation
  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_URL}hrms/role/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data.success) {
        // Remove the deleted role from the state
        setRoles((prevRoles) =>
          prevRoles.filter((role) => role.id !== id)
        );
        Swal.fire("Deleted!", "Role has been deleted.", "success");
        fetchRoles(); // Refresh the list
      } else {
        Swal.fire("Error!", "Failed to delete the role.", "error");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      Swal.fire("Error!", "Failed to delete the role.", "error");
    }
  };

  // Calculate start and end indices for displaying records
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalRecords);


  useEffect(() => {
    const handleClearDateFilters = () => {
      setSearch("");
    };

    window.addEventListener(
      "clearRoleManagementFiltersEvent",
      handleClearDateFilters
    );

    return () => {
      window.removeEventListener(
        "clearRoleManagementFiltersEvent",
        handleClearDateFilters
      );
    };
  }, []);

  return (
    <div id="" className="mt-5">
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="panel-heading border-0 mb-3">
          <div className="row">
            <div className="col-md-6">
              <h4 className="panel-title pull-left">Manage Role</h4>
            </div>
            <div className="col-md-6" style={{ textAlign: "right" }}>
              <PermissionWrapper module="hrms_role" action="add">
                <Link
                  to="/Hrms/Role/add"
                  className="btn btn-default btn-sm me-2"
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Role
                </Link>
              </PermissionWrapper>

              {/* Pagination Controls */}
              {roles.length > 0 && (
                <div className="d-inline-block">
                  <span style={{ fontSize: "0.8rem" }} className="me-2">
                    {start} to {end} of {totalRecords}
                  </span>
                  <button
                    className="btn btn-default btn-sm"
                    title="Previous"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-default btn-sm"
                    title="Next"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="row mb-3 mx-2">
          <div className="col-4">
            <div className="input-group input-group-sm">
              <input
                type="text"
                className="form-control"
                placeholder="Search roles..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="table-responsive user-table-container">
            <table className="table m-b-0">
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: "5%",
                    }}
                  >
                    #
                  </th>
                  <th>Name</th>
                  <th style={{ width: "1%" }} colSpan="2">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="skeleton-row">
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <div className="skeleton-box" />
                    </td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                    <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : roles.length > 0 ? (
          <div className="table-responsive user-table-container">
            <style>
              {`
                  .user-table-container {
                    position: relative;
                    max-height: 67vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: 0;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
            </style>
            <table className="table m-b-0">
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: "5%",
                    }}
                  >
                    #
                  </th>
                  <th>Name</th>
                  <th style={{ width: "1%" }} colSpan="2">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role, index) => (
                  <tr key={role.id}>
                    <td
                      style={{ textAlign: "center", verticalAlign: "middle" }}
                    >
                      {index + 1 + (page - 1) * limit}
                    </td>
                    <td>{role.user_role}</td>
                    <td>
                      <PermissionWrapper module="hrms_role" action="edit">
                        <Link
                          to={`/Hrms/Role/edit/${role.id}`}
                          className="btn btn-warning btn-xs"
                          title="Edit Role"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                      </PermissionWrapper>
                    </td>
                    <td>
                      <PermissionWrapper module="hrms_role" action="delete">
                        <button
                          className="btn btn-danger btn-xs"
                          onClick={() => handleDeleteConfirm(role.id)}
                          title="Delete Role"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </PermissionWrapper>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="alert alert-warning m-3">
            No roles found. <Link to="/Hrms/Role/add">Add a new role.</Link>
          </div>
        )}

        {/* Items Per Page Control */}
        {/* <div className="mx-3 mt-2">
          <label>
            Items per page: 
            <select
              value={limit}
              onChange={handleLimitChange}
              className="form-control d-inline-block w-auto ml-2"
            >
              {[10, 25, 50, 100].map((limitOption) => (
                <option key={limitOption} value={limitOption}>
                  {limitOption}
                </option>
              ))}
            </select>
          </label>
        </div> */}
      </div>

      {/* Scroll to Top Button */}
      <Link href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </Link>
    </div>
  );
};

export default RoleManager;